import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Divider } from "primereact/divider";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { Password } from "primereact/password";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { RadioButton } from "primereact/radiobutton";
import { Calendar } from "primereact/calendar";
import { InputSwitch } from "primereact/inputswitch";
import { FileUpload } from "primereact/fileupload";
import { InputTextarea } from "primereact/inputtextarea";
import { Editor } from "primereact/editor";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import api from "../../Services/api";
import Loader from "../../AuthContext/Preloader";
import LinkNotFound from "../../Components/404LinkNotFound";
import UnauthorizedAccess from "./UnauthorizedAccess";
import SuccsessfullFormSubmit from "../../pages/GenerateForms/SuccessfullFormSubmit";
import usePageTitle from "../usePageTitle";
import MapDialog from "../GenerateForms/MapDialog";
import { MultiSelect } from "primereact/multiselect";

const UserFormFillPage = () => {
  usePageTitle("Fill Form | SkeyeForm™");
  const { formId } = useParams();
  const [form, setForm] = useState(null);
  const [formData, setFormData] = useState({ data: {} });
  const [formStatus, setFormStatus] = useState("loading");
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [showSuccessPage, setShowSuccessPage] = useState(false);
  const [password, setPassword] = useState("");
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  // eslint-disable-next-line
  const [userHasAccess, setUserHasAccess] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mapDialogVisible, setMapDialogVisible] = useState(false);
  const [selectedMapField, setSelectedMapField] = useState(null);
  const [filteredOptions, setFilteredOptions] = useState({});

  const labelToPropName = useMemo(() => {
    if (!form || !form.properties) return {};
    return Object.entries(form.properties).reduce((acc, [propName, prop]) => {
      if (prop?.key) acc[prop.key] = propName;
      return acc;
    }, {});
  }, [form]);

  // Resolve either a label or already a property name
  const resolvePropName = (maybeLabel) =>
    labelToPropName[maybeLabel] || maybeLabel;

  const handleOpenMapDialog = (key) => {
    setSelectedMapField(key);
    setMapDialogVisible(true);
  };

  const handleSaveCoordinates = (coords) => {
    setFormData((prevData) => ({
      ...prevData,
      data: {
        ...prevData.data,
        [selectedMapField]: coords,
      },
    }));
  };

  const toast = useRef(null);

  const checkUserFormAccess = async (formId) => {
    try {
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");

      const userId = String(userData.id);

      const response = await api.get(`/forms/user/${userId}`);

      const userForms = (response.data.forms || []).filter(
        (form) => !form.softDelete
      );
      const hasAccess = userForms.some((form) => form.id === formId);

      return hasAccess;
    } catch (error) {
      console.error("Error checking user form access:", error);
      return false;
    }
  };

  useEffect(() => {
    const fetchForm = async () => {
      try {
        // First check if user has access to this form
        const hasAccess = await checkUserFormAccess(formId);

        if (!hasAccess) {
          setUserHasAccess(false);
          setFormStatus("unauthorized");
          return;
        }

        const response = await api.get(`/forms/fill/${formId}`);

        if (response.data.status === "down") {
          setFormStatus("down");
        } else {
          setForm(response.data.form);
          setFormStatus("up");
          if (response.data.form.password) {
            setIsPasswordModalVisible(true);
          }
        }
      } catch (error) {
        console.error("Error fetching form:", error);
        setFormStatus("down");
      }
    };

    fetchForm();
  }, [formId]);

  useEffect(() => {
    if (form && form.properties) {
      setFormData((prevData) => {
        const newData = { ...prevData.data };

        Object.entries(form.properties).forEach(([key, field]) => {
          if (field.type === "inputswitch" && newData[key] === undefined) {
            newData[key] = false;
          }
        });

        return {
          ...prevData,
          data: newData,
        };
      });
    }
  }, [form]);

  useEffect(() => {
    if (isFormSubmitted) {
      const timer = setTimeout(() => {
        setShowSuccessPage(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isFormSubmitted]);

  // Synchronize dropdowns based on depsConf and depsResult
  useEffect(() => {
    if (
      form &&
      form.depsConf &&
      form.depsResult &&
      form.depsConf.length > 0 &&
      form.properties
    ) {
      form.depsConf.forEach((dep) => {
        const parentLabel =
          dep.parent?.currentFieldKey || dep.Parent?.currentFieldKey;
        const childLabel =
          dep.child?.currentFieldKey || dep.Child?.currentFieldKey;
        if (!parentLabel || !childLabel) return;

        const parentProp = resolvePropName(parentLabel);
        const childProp = resolvePropName(childLabel);
        if (!form.properties[childProp]) return;

        const parentValue = formData.data[parentProp];

        if (parentValue !== undefined && parentValue !== "") {
          const match = form.depsResult.find(
            (r) => r[parentLabel] === parentValue
          );
          if (
            match &&
            Array.isArray(match[`${childLabel}`]) &&
            form.properties[childProp].options
          ) {
            const allowed = new Set(match[`${childLabel}`]);
            setFilteredOptions((prev) => ({
              ...prev,
              [childProp]: form.properties[childProp].options.filter((opt) =>
                allowed.has(opt.value)
              ),
            }));
            return;
          }
        }
        // Fallback: all options
        setFilteredOptions((prev) => ({
          ...prev,
          [childProp]: form.properties[childProp].options || [],
        }));
      });
    } // eslint-disable-next-line
  }, [form, formData.data, labelToPropName]);

  if (formStatus === "loading") {
    return <Loader />;
  }

  if (formStatus === "unauthorized") {
    return <UnauthorizedAccess />;
  }

  if (formStatus === "down") {
    return <LinkNotFound />;
  }

  const handlePasswordSubmit = async () => {
    try {
      const response = await api.post(`/forms/check-password/${formId}`, {
        password,
      });
      if (response.data.message === "Password matches") {
        setIsPasswordModalVisible(false);
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Password is correct",
          life: 3000,
        });
      } else if (response.data.message === "Password does not match") {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Incorrect password",
          life: 3000,
        });
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data.message === "Password does not match"
      ) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Incorrect password",
          life: 3000,
        });
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "An error occurred while checking the password",
          life: 3000,
        });
      }
    }
  };
  const handleCheckboxChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData((prevData) => {
      const newValues = prevData.data[name] ? [...prevData.data[name]] : [];
      if (checked) {
        newValues.push(value);
      } else {
        const index = newValues.indexOf(value);
        if (index > -1) {
          newValues.splice(index, 1);
        }
      }
      const updatedData = {
        ...prevData.data,
        [name]: newValues,
      };
      return {
        ...prevData,
        data: updatedData,
      };
    });
  };

  const handleMultiSelectChange = (e) => {
    const { name, value } = e.target || {
      name: e.originalEvent?.target?.name,
      value: e.value,
    };
    setFormData((prev) => {
      let newData = { ...prev.data, [name]: value };
      if (form?.depsConf) {
        form.depsConf.forEach((dep) => {
          const parentLabel =
            dep.parent?.currentFieldKey || dep.Parent?.currentFieldKey;
          const childLabel =
            dep.child?.currentFieldKey || dep.Child?.currentFieldKey;
          if (!parentLabel || !childLabel) return;
          const parentProp = resolvePropName(parentLabel);
          const childProp = resolvePropName(childLabel);

          if (name === parentProp) {
            newData[childProp] = Array.isArray(newData[childProp]) ? [] : "";
          }

          if (name === childProp) {
            const selected = value;
            const currentParentValue = newData[parentProp];

            const matches =
              form.depsResult?.filter((r) => {
                const arr = r[`${childLabel}`];
                if (!Array.isArray(arr)) return false;
                if (Array.isArray(selected))
                  return selected.some((sv) => arr.includes(sv));
                return arr.includes(selected);
              }) || [];

            if (matches.length > 0) {
              // Check if current parent is valid for this child selection
              const currentParentIsValid = matches.some(
                (m) => m[parentLabel] === currentParentValue
              );

              // Only auto-fill if current parent is not valid
              if (!currentParentIsValid) {
                let correctMatch = matches[0];

                if (matches.length > 1) {
                  const grandParentDep = form.depsConf.find((d) => {
                    const childKey =
                      d.child?.currentFieldKey || d.Child?.currentFieldKey;
                    return resolvePropName(childKey) === parentProp;
                  });

                  if (grandParentDep) {
                    const grandParentLabel =
                      grandParentDep.parent?.currentFieldKey ||
                      grandParentDep.Parent?.currentFieldKey;
                    const grandParentProp = resolvePropName(grandParentLabel);
                    const grandParentValue = newData[grandParentProp];

                    if (grandParentValue) {
                      const validParents = form.depsResult?.find(
                        (r) => r[grandParentLabel] === grandParentValue
                      );

                      if (
                        validParents &&
                        Array.isArray(validParents[`${parentLabel}s`])
                      ) {
                        correctMatch =
                          matches.find((m) =>
                            validParents[`${parentLabel}s`].includes(
                              m[parentLabel]
                            )
                          ) || correctMatch;
                      }
                    }
                  }
                }

                if (correctMatch && correctMatch[parentLabel] !== undefined) {
                  newData[parentProp] = correctMatch[parentLabel];
                }
              }
            }
          }
        });
      }
      return { ...prev, data: newData };
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      data: {
        ...prevData.data,
        [name]: type === "checkbox" ? checked : value,
      },
    }));
  };

  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      let newData = { ...prev.data, [name]: value };
      if (form?.depsConf) {
        form.depsConf.forEach((dep) => {
          const parentLabel =
            dep.parent?.currentFieldKey || dep.Parent?.currentFieldKey;
          const childLabel =
            dep.child?.currentFieldKey || dep.Child?.currentFieldKey;
          if (!parentLabel || !childLabel) return;
          const parentProp = resolvePropName(parentLabel);
          const childProp = resolvePropName(childLabel);

          if (name === parentProp) {
            newData[childProp] = "";
          }

          // If changing child, auto-fill parent ONLY if current parent is invalid
          if (name === childProp) {
            const currentParentValue = newData[parentProp];

            const matches =
              form.depsResult?.filter(
                (r) =>
                  Array.isArray(r[`${childLabel}`]) &&
                  r[`${childLabel}`].includes(value)
              ) || [];

            if (matches.length > 0) {
              // Check if current parent is valid for this child
              const currentParentIsValid = matches.some(
                (m) => m[parentLabel] === currentParentValue
              );

              // Only auto-fill if current parent is not valid
              if (!currentParentIsValid) {
                let correctMatch = matches[0];

                if (matches.length > 1) {
                  const grandParentDep = form.depsConf.find((d) => {
                    const childKey =
                      d.child?.currentFieldKey || d.Child?.currentFieldKey;
                    return resolvePropName(childKey) === parentProp;
                  });

                  if (grandParentDep) {
                    const grandParentLabel =
                      grandParentDep.parent?.currentFieldKey ||
                      grandParentDep.Parent?.currentFieldKey;
                    const grandParentProp = resolvePropName(grandParentLabel);
                    const grandParentValue = newData[grandParentProp];

                    if (grandParentValue) {
                      const validParents = form.depsResult?.find(
                        (r) => r[grandParentLabel] === grandParentValue
                      );

                      if (
                        validParents &&
                        Array.isArray(validParents[`${parentLabel}s`])
                      ) {
                        correctMatch =
                          matches.find((m) =>
                            validParents[`${parentLabel}s`].includes(
                              m[parentLabel]
                            )
                          ) || correctMatch;
                      }
                    }
                  }
                }

                if (correctMatch && correctMatch[parentLabel] !== undefined) {
                  newData[parentProp] = correctMatch[parentLabel];
                }
              }
            }
          }
        });
      }
      return { ...prev, data: newData };
    });
  };

  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      data: {
        ...prevData.data,
        [name]: value,
      },
    }));
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      data: {
        ...prevData.data,
        [name]: checked,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);

    const requiredFields = Object.entries(form.properties).filter(
      ([key, field]) => field.required === "yes"
    );
    // eslint-disable-next-line
    for (const [key, field] of requiredFields) {
      if (
        !formData.data[key] ||
        (Array.isArray(formData.data[key]) && formData.data[key].length === 0)
      ) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: `Please fill the required field: ${field.key}`,
          life: 3000,
        });
        setIsSubmitting(false);
        return;
      }
    }

    const minFields = Object.entries(form.properties).filter(
      ([key, field]) => field.min !== undefined
    );
    // eslint-disable-next-line
    for (const [key, field] of minFields) {
      const value = formData.data[key];
      if (value && Number(value) < Number(field.min)) {
        toast.current.show({
          severity: "error",
          summary: "Validation Error",
          detail: `Value for ${field.key} must be at least ${field.min}`,
          life: 3000,
        });
        setIsSubmitting(false);
        return;
      }
    }

    const allowedFileTypes = {
      image: ["png", "jpeg", "jpg", "gif", "bmp", "svg"],
      video: ["mp4", "avi", "mov", "wmv", "flv", "mkv"],
      json: ["json"],
      csv: ["csv"],
      pdf: ["pdf"],
    };

    const fileUploads = [];
    for (const [key, value] of Object.entries(formData.data)) {
      if (value instanceof File) {
        const fileType = form.properties[key]?.fileType || "all";
        const fileExtension = value.name.split(".").pop().toLowerCase();

        if (
          fileType !== "all" &&
          (!allowedFileTypes[fileType] ||
            !allowedFileTypes[fileType].includes(fileExtension))
        ) {
          toast.current.show({
            severity: "error",
            summary: "File Type Error",
            detail: `Invalid file type for field ${key}. Expected ${fileType}, but got ${fileExtension}.`,
            life: 3000,
          });
          setIsSubmitting(false);
          return;
        }

        const fileFormData = new FormData();
        fileFormData.append("file", value, value.name);

        try {
          const uploadResponse = await api.post(
            "/filled-forms/upload",
            fileFormData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );

          if (uploadResponse.data.message === "File uploaded successfully") {
            fileUploads.push({
              field: key,
              filepath: uploadResponse.data.filePath,
              uniqueFolder: uploadResponse.data.uniqueFolder,
            });
          }
        } catch (error) {
          console.error(
            "File upload error:",
            error.response?.data || error.message
          );
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: `Failed to upload file: ${
              error.response?.data?.message || error.message
            }`,
            life: 3000,
          });
          setIsSubmitting(false);
          return;
        }
      }
    }

    const formattedData = Object.entries(formData.data).reduce(
      (acc, [key, value]) => {
        const fieldType = form.properties[key]?.type;

        if (value instanceof File) {
          const fileUpload = fileUploads.find((f) => f.field === key);
          acc[key] = {
            value: fileUpload ? fileUpload.filepath : null,
            type: fieldType,
            key: form.properties[key].key,
            uniqueFolder: fileUpload ? fileUpload.uniqueFolder : null,
          };
        } else if (fieldType === "geolocation" && Array.isArray(value)) {
          acc[key] = {
            value: value.map((pos) => ({
              latitude: pos.lat,
              longitude: pos.lng,
              address: pos.address,
            })),
            type: fieldType,
            key: form.properties[key].key,
          };
        } else {
          acc[key] = {
            value,
            type: fieldType,
            key: form.properties[key].key,
          };
        }
        return acc;
      },
      {}
    );

    const payload = {
      formSchemaId: formId,
      label: form.label,
      formTitle: form.title,
      createdAt: new Date().toISOString(),
      data: formattedData,
    };
    const linkedFields = Object.entries(form.properties).filter(
      ([key, field]) =>
        ["text", "email", "number", "date"].includes(field.type) && field.linked
    );

    for (const [key, field] of linkedFields) {
      try {
        const response = await api.get(`/filled-forms/formSchemaId/${formId}`);

        const filledForms = response.data.filledForms;

        if (!filledForms || !Array.isArray(filledForms)) {
          console.warn("No filled forms found or invalid response format");
          continue;
        }

        const valueExists = filledForms.some((form) => {
          const formValue = form.data[key]?.value;
          const inputValue = formData.data[key]?.value;

          if (field.type === "date") {
            const formDate = new Date(formValue).toISOString();
            const inputDate = new Date(inputValue).toISOString();
            return formDate === inputDate;
          }

          return formValue === inputValue;
        });

        if (!valueExists) {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail:
              "Please read the description, you seem to set something wrong",
            life: 3000,
          });
          setIsSubmitting(false);
          return;
        }
      } catch (error) {
        console.error("Error fetching filled forms:", error);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Error validating form data",
          life: 3000,
        });
        setIsSubmitting(false);
        return;
      }
    }

    try {
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      const userId = userData.id;

      const finalPayload = {
        ...payload,
        submissionId: userId,
      };

      await api.post("/filled-forms/create", finalPayload);
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Form submitted successfully",
        life: 3000,
      });
      setIsFormSubmitted(true);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "An error occurred";
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: errorMessage,
        life: 3000,
      });
      setIsSubmitting(false);
    }
  };

  if (showSuccessPage) {
    return <SuccsessfullFormSubmit />;
  }

  const renderField = (propName, field) => {
    const labelStyle = { fontWeight: "600", marginBottom: "0.5rem" };
    const fieldStyle = { marginBottom: "1.5rem" };

    const isChildInDeps =
      form?.depsConf?.some((dep) => {
        const childLabel =
          dep.child?.currentFieldKey || dep.Child?.currentFieldKey;
        const childProp = resolvePropName(childLabel);
        return childProp === propName;
      }) || false;

    switch (field.type) {
      case "text":
      case "email":
      case "number":
        return (
          <div key={propName} className="p-field" style={fieldStyle}>
            <label htmlFor={propName} style={labelStyle}>
              {field.key}
            </label>
            <InputText
              id={propName}
              name={propName}
              type={field.type}
              maxLength={field.maxLength || undefined}
              min={field.min || undefined}
              max={field.max || undefined}
              required={field.required === "yes"}
              style={{ marginTop: "0.5rem" }}
              value={formData.data[propName] || ""}
              onChange={handleInputChange}
            />
          </div>
        );
      case "geolocation":
        return (
          <div key={propName} className="p-field" style={fieldStyle}>
            <label htmlFor={propName} style={labelStyle}>
              {field.key}
            </label>
            <Button
              type="button"
              label={
                formData.data[propName]
                  ? `Selected: ${formData.data[propName][0]}, ${formData.data[propName][1]}`
                  : "Select Coordinates"
              }
              icon="pi pi-map-marker"
              onClick={() => handleOpenMapDialog(propName)}
              className="w-full mb-3"
              style={{
                backgroundColor: "#609856",
                color: "#fff",
                border: "none",
              }}
            />
          </div>
        );
      case "checkbox":
        return (
          <div key={propName} className="p-field-checkbox" style={fieldStyle}>
            <label htmlFor={propName} style={labelStyle}>
              {field.key}
            </label>
            {(field.options || []).map((option, index) => (
              <div key={index} style={{ marginTop: "0.5rem" }}>
                <Checkbox
                  inputId={`${propName}_${index}`}
                  name={propName}
                  value={option.value}
                  onChange={handleCheckboxChange}
                  checked={
                    formData.data[propName] &&
                    formData.data[propName].includes(option.value)
                  }
                />
                <label
                  htmlFor={`${propName}_${index}`}
                  style={{ marginLeft: "0.5rem" }}
                >
                  {option.option}
                </label>
              </div>
            ))}
          </div>
        );
      case "dropdown": {
        const optionsToShow = isChildInDeps
          ? filteredOptions[propName] || field.options || []
          : field.options || [];
        return (
          <div key={propName} className="p-field" style={fieldStyle}>
            <label htmlFor={propName} style={labelStyle}>
              {field.key}
            </label>
            <Dropdown
              id={propName}
              name={propName}
              filter
              style={{ marginTop: "0.5rem" }}
              options={optionsToShow.map((o) => ({
                label: o.option,
                value: o.value,
              }))}
              onChange={handleDropdownChange}
              value={formData.data[propName] || ""}
            />
          </div>
        );
      }
      case "radio":
        return (
          <div
            key={propName}
            className="p-field-radiobutton"
            style={fieldStyle}
          >
            <label style={labelStyle}>{field.key}</label>
            {(field.options || []).map((option, index) => (
              <div key={index}>
                <RadioButton
                  inputId={`${propName}_${index}`}
                  name={propName}
                  value={option.value}
                  style={{ marginTop: "0.5rem" }}
                  onChange={handleRadioChange}
                  checked={formData.data[propName] === option.value}
                />
                <label htmlFor={`${propName}_${index}`}>{option.option}</label>
              </div>
            ))}
          </div>
        );
      case "date":
        return (
          <div key={propName} className="p-field" style={fieldStyle}>
            <label htmlFor={propName} style={labelStyle}>
              {field.key}
            </label>
            <Calendar
              id={propName}
              name={propName}
              style={{ marginTop: "0.5rem" }}
              onChange={(e) => {
                const date = e.value;
                if (date) {
                  const formattedDate = new Date(
                    date.toLocaleString("en-US", {
                      timeZone: "Africa/Tunis",
                    })
                  );
                  formattedDate.setHours(12, 0, 0, 0);
                  const isoString = formattedDate.toISOString();
                  const tunisiaOffset = "+01:00";
                  const formattedISOString =
                    isoString.slice(0, -5) + tunisiaOffset;
                  handleInputChange({
                    target: { name: propName, value: formattedISOString },
                  });
                } else {
                  handleInputChange({
                    target: { name: propName, value: null },
                  });
                }
              }}
              value={
                formData.data?.[propName]
                  ? new Date(formData.data[propName])
                  : null
              }
              showTime={false}
              timeZone="Africa/Tunis"
            />
          </div>
        );
      case "inputswitch":
        return (
          <div key={propName} className="p-field" style={fieldStyle}>
            <label htmlFor={propName} style={labelStyle}>
              {field.key}
            </label>
            <div style={{ marginTop: "0.5rem", marginRight: "0.5rem" }}>
              <InputSwitch
                id={propName}
                name={propName}
                onChange={(e) =>
                  handleSwitchChange({
                    target: { name: propName, checked: e.value },
                  })
                }
                checked={formData.data[propName] ?? false}
              />
            </div>
          </div>
        );
      case "fileupload":
        return (
          <div key={propName} className="p-field" style={fieldStyle}>
            <label htmlFor={propName} style={labelStyle}>
              {field.key}
            </label>
            <FileUpload
              id={propName}
              name={propName}
              style={{ marginTop: "0.5rem" }}
              auto={false}
              customUpload
              mode="advanced"
              onSelect={(e) => {
                setFormData((prevData) => ({
                  ...prevData,
                  data: {
                    ...prevData.data,
                    [propName]: e.files[0],
                  },
                }));
              }}
              chooseOptions={{
                className: "p-button p-component",
                style: {
                  backgroundColor: "#e29b45",
                  color: "#fff",
                  border: "none",
                },
              }}
              cancelOptions={{
                className: "p-button p-component",
                style: {
                  backgroundColor: "#e29b45",
                  color: "#fff",
                  border: "none",
                },
              }}
              uploadOptions={{
                className: "p-button p-component p-disabled",
                icon: "none",
                style: {
                  backgroundColor: "#F8F9FA",
                  color: "#F8F9FA",
                  border: "none",
                  width: "0px",
                  height: "0px",
                  padding: "0px",
                  margin: "0px",
                },
              }}
              showUploadButton={false}
            />
          </div>
        );
      case "multiselect": {
        const optionsToShow = isChildInDeps
          ? filteredOptions[propName] || field.options || []
          : field.options || [];
        return (
          <div key={propName} className="p-field" style={fieldStyle}>
            <label htmlFor={propName} style={labelStyle}>
              {field.key}
            </label>
            <MultiSelect
              id={propName}
              name={propName}
              options={optionsToShow.map((o) => ({
                label: o.option,
                value: o.value,
              }))}
              value={formData.data[propName] || []}
              onChange={(e) =>
                handleMultiSelectChange({
                  originalEvent: { target: { name: propName } },
                  value: e.value,
                })
              }
              style={{ marginTop: "0.5rem" }}
              filter
            />
          </div>
        );
      }
      case "textarea":
        return (
          <div key={propName} className="p-field" style={fieldStyle}>
            <label htmlFor={propName} style={labelStyle}>
              {field.key}
            </label>
            <InputTextarea
              id={propName}
              name={propName}
              style={{ marginTop: "0.5rem" }}
              onChange={handleInputChange}
              value={formData.data[propName] || ""}
            />
          </div>
        );
      case "description":
        return (
          <div key={propName} className="p-field" style={fieldStyle}>
            <label htmlFor={propName} style={labelStyle}>
              {field.key}
            </label>
            <Editor
              id={propName}
              value={field.value}
              readOnly
              style={{ marginTop: "0.5rem" }}
            />
          </div>
        );
      default:
        return null;
    }
  };

  const renderGroupedFields = () => {
    const groupedFields = {};
    Object.entries(form.properties).forEach(([key, field]) => {
      const group = field.group || "ungrouped";
      if (!groupedFields[group]) {
        groupedFields[group] = [];
      }
      groupedFields[group].push({ key, field });
    });

    return Object.entries(groupedFields).map(([group, fields]) => {
      const fieldsPerRow = fields[0].field.fieldsPerRow || 1;
      const flexBasis = `calc(${100 / fieldsPerRow}% - 1rem)`;

      return (
        <React.Fragment key={group}>
          {group !== "ungrouped" &&
            Object.values(form.properties).some(
              (field) => field.group === group && field.renderHeader === true
            ) && (
              <h3 style={{ width: "100%", marginBottom: "1rem" }}>{group}</h3>
            )}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "1rem",
              marginBottom: "1rem",
            }}
          >
            {fields.map(({ key, field }) => (
              <React.Fragment key={key}>
                <div
                  style={{
                    flex:
                      group === "ungrouped" ? "1 1 100%" : `1 1 ${flexBasis}`,
                    minWidth: group === "ungrouped" ? "100%" : "200px",
                  }}
                >
                  {renderField(key, field)}
                </div>
                {group === "ungrouped" && <Divider />}
              </React.Fragment>
            ))}
          </div>

          {group !== "ungrouped" && <Divider />}
        </React.Fragment>
      );
    });
  };

  return (
    <div className="p-grid p-dir-col table-page">
      <Toast ref={toast} />
      <Dialog
        header="Enter Password"
        visible={isPasswordModalVisible}
        modal
        closable={false}
        dismissableMask={false}
        style={{ width: window.innerWidth > 768 ? "60%" : "90%" }}
      >
        <div className="p-field p-fluid">
          <Password
            toggleMask
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            feedback={false}
          />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            label="Submit"
            onClick={handlePasswordSubmit}
            style={{
              backgroundColor: "#e29b45",
              color: "#fff",
              border: "none",
            }}
          />
        </div>
      </Dialog>
      <div
        className="p-col title"
        style={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <img
          src="/img/majesteye-icon-skeye-form-horizontal-white-background.svg"
          alt="SkeyeForm Logo"
          style={{
            height: "50px",
            alignSelf: "flex-start",
            marginBottom: "2rem",
          }}
        />
        <h1 style={{ margin: 0, fontSize: "1.5rem" }}>Fill Form</h1>
        <div className="title-bar"></div>
      </div>
      <div
        className="table-container"
        style={{ width: window.innerWidth > 768 ? "95%" : "98%" }}
      >
        <div className="p-fluid">
          {form && (
            <>
              <h2 style={{ textAlign: "center" }}>{form.title}</h2>
              <Divider />
              <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                {form.description}
              </ReactMarkdown>
              <Divider />
              <form onSubmit={handleSubmit}>
                {renderGroupedFields()}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <Button
                    label={isSubmitting ? "Submitting..." : "Submit"}
                    icon={
                      isSubmitting ? "pi pi-spin pi-spinner" : "pi pi-check"
                    }
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      marginTop: "20px",
                      width: "150px",
                      justifyContent: "center",
                      backgroundColor: "#0D2E4D",
                      border: "none",
                    }}
                  />
                </div>
              </form>
            </>
          )}
        </div>
      </div>
      {mapDialogVisible && (
        <MapDialog
          visible={mapDialogVisible}
          onHide={() => setMapDialogVisible(false)}
          onSave={handleSaveCoordinates}
          initialValue={formData.data[selectedMapField]}
        />
      )}
    </div>
  );
};

export default UserFormFillPage;
